import React, { useEffect, useId, useRef } from 'react';
import { Box, Button, Drawer, IconButton, Popover, PopoverActions, Typography, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

interface FilterSurfaceProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  title: string;
  /** "Clear", shown while the filter is set. */
  onClear?: () => void;
  /** A back arrow, for an editor reached from "More filters". */
  onBack?: () => void;
  /** How many contacts match now, so each change shows its effect without closing. */
  matchCount?: string;
  /** On a wide screen. On a phone the panel is a sheet across the bottom. */
  width?: number;
  children: React.ReactNode;
}

/**
 * The panel a filter pill opens. Changes apply as they are made, so "Done" only
 * closes it; Escape and a click outside do the same.
 */
export const FilterSurface = ({ open, anchorEl, onClose, title, onClear, onBack, matchCount, width = 360, children }: FilterSurfaceProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const titleId = useId();
  const popover = useRef<PopoverActions>(null);

  // A different panel in the same popover ("More filters" to one of its filters): place it again, so a wider one still fits.
  useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(() => popover.current?.updatePosition());
    return () => window.cancelAnimationFrame(frame);
  }, [open, title, width]);

  const body = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, pt: 1.5, pb: 1 }}>
        {onBack && (
          <IconButton size="small" aria-label="Back to more filters" onClick={onBack} sx={{ ml: -1 }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
        <Typography id={titleId} variant="subtitle1" component="h2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {onClear && (
          <Button size="small" onClick={onClear} sx={{ minWidth: 0, px: 1 }}>
            Clear
          </Button>
        )}
      </Box>

      <Box sx={{ px: 2, pb: 2, overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>{children}</Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f8fafc',
        }}
      >
        <Typography variant="body2" color="text.secondary" aria-live="polite" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {matchCount}
        </Typography>
        <Button size="small" variant="contained" onClick={onClose}>
          Done
        </Button>
      </Box>
    </>
  );

  if (phone) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          'aria-labelledby': titleId,
          role: 'dialog',
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '88vh', display: 'flex', flexDirection: 'column' },
        }}
      >
        <Box aria-hidden sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#cbd5e1', mx: 'auto', mt: 1 }} />
        {body}
      </Drawer>
    );
  }

  return (
    <Popover
      action={popover}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      marginThreshold={16}
      PaperProps={{
        'aria-labelledby': titleId,
        role: 'dialog',
        sx: {
          width,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'min(640px, calc(100vh - 96px))',
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 16px 40px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.06)',
        },
      }}
    >
      {body}
    </Popover>
  );
};
